import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../contexts/HospitalOnboardingContext';
import { useAuth } from '../../contexts/AuthContext';
import { ISTClock, NotificationDropdown, DropdownArrowIcon } from '../Topbar';
import LogoutIcon from '../../svg/logout.svg';
import GroupSvg from '../../svg/Group.svg';
import Modal from '../requests/Modal';
import { RotateCcw, PhoneCall, User, LogOut } from 'lucide-react';

import { hospitalOnboardingService } from '../../services/hospitalOnboardingService';

export const OnboardingHeader = ({
  title = 'RISA Smart Ambulance',
  subtitle = 'Manage hospital & fleet onboarding, profile verification, and live activation.',
  userRole = 'onboard_user'
}) => {
  const navigate = useNavigate();
  const { startOver } = useHospitalOnboarding();
  const { user, logout } = useAuth();
  
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    try {
      const userId = user?.id || user?._id || user?.userId;
      hospitalOnboardingService.clearDraft(userId);
      if (logout) {
        await logout();
      }
      navigate('/login', { replace: true });
    } catch (error) {
      navigate('/login', { replace: true });
    }
  };

  const handleConfirmStartOver = () => {
    startOver();
    setShowStartOverModal(false);
    navigate('/hospital/onboarding');
  };

  return (
    <>
      <header className="w-full sticky top-0 z-40 px-3 sm:px-6 py-2 sm:py-2.5 transition-all">
        {/* Full-width Rounded Glassmorphic Topbar matching Dashboard Theme */}
        <div 
          className="w-full flex items-center justify-between gap-2 sm:gap-4 p-2.5 sm:p-3 rounded-xl border border-[#1FCDFF40]/30 shadow-xs flex-wrap"
          style={{
            background: 'linear-gradient(135deg, rgba(248, 249, 250, 0.96) 0%, rgba(227, 242, 253, 0.92) 100%)',
            backdropFilter: 'blur(40px) saturate(180%)'
          }}
        >
          {/* Left Side: Logo + Title + Subtitle */}
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <div 
              onClick={() => navigate('/hospital/onboarding')}
              className="flex items-center cursor-pointer select-none group"
              title="RISA Smart Ambulance Home"
            >
              <img 
                src={GroupSvg} 
                alt="RISA Smart Ambulance Logo" 
                className="h-8 sm:h-9 w-auto max-w-[140px] sm:max-w-[170px] object-contain transition-transform group-hover:scale-[1.02]" 
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/logo.svg';
                }} 
              />
            </div>

            <div className="h-7 w-px bg-slate-300/60 hidden sm:block" />

            <div className="flex flex-col justify-between flex-shrink-0">
              <h1 className="text-sm sm:text-base font-semibold text-slate-800 whitespace-nowrap">
                {title}
              </h1>
              <p className="text-[10px] sm:text-xs font-normal text-slate-500 whitespace-nowrap hidden sm:block">
                {subtitle}
              </p>
            </div>
          </div>

          {/* Right Side: IST Clock, Notification Bell, Dropdown Arrow, Logout Icon */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-auto">
            {/* Live IST Clock */}
            <ISTClock />

            {/* Notification Bell Dropdown */}
            <NotificationDropdown userRole={userRole} />

            {/* Profile / Options Dropdown Trigger */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="p-1 sm:p-2 hover:opacity-70 transition-all cursor-pointer rounded-lg hover:bg-white/50"
                aria-label="Toggle user menu"
                title="Account & Options"
              >
                <DropdownArrowIcon />
              </button>

              {/* User Dropdown Popover */}
              {showUserDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200/80 p-2 z-50 animate-fade-in text-left">
                  {/* User Profile Header */}
                  <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100 mb-1.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="overflow-hidden">
                        <p className="text-xs font-semibold text-slate-800 truncate">
                          {user?.email || 'onboard@smartambulance.com'}
                        </p>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200/60 mt-0.5">
                          {user?.role || 'Onboard Partner'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Helpline Contact Link */}
                  <a
                    href="tel:+919904104104"
                    className="flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:text-blue-700 hover:bg-blue-50/80 rounded-xl transition-colors"
                  >
                    <PhoneCall className="w-3.5 h-3.5 text-blue-600" />
                    <div>
                      <span className="block font-medium text-[11px] leading-tight">Need help?</span>
                      <span className="text-[11px] text-slate-500 leading-tight">+91 9904 104 104</span>
                    </div>
                  </a>

                  {/* Start Over Action */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserDropdown(false);
                      setShowStartOverModal(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer text-left"
                  >
                    <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
                    <span>Start over (Clear draft)</span>
                  </button>

                  <div className="h-px bg-slate-100 my-1" />

                  {/* Logout Item */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserDropdown(false);
                      setShowLogoutModal(true);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-colors cursor-pointer text-left"
                  >
                    <LogOut className="w-3.5 h-3.5 text-red-600" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

            {/* Direct Logout Icon Button (Identical to Dashboard Topbar) */}
            <button
              type="button"
              onClick={handleLogoutClick}
              className="p-1 sm:p-2 hover:opacity-70 transition-all cursor-pointer rounded-lg hover:bg-white/50"
              aria-label="Logout"
              title="Logout"
            >
              <img 
                src={LogoutIcon} 
                alt="Logout" 
                className="w-5 h-5 sm:w-6 sm:h-6" 
              />
            </button>
          </div>
        </div>
      </header>

      {/* Logout Confirmation Modal (Consistent with Dashboard Topbar Modal) */}
      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        title="Confirm Logout"
      >
        <div className="space-y-4">
          <p className="text-gray-700 text-base">
            Are you sure you want to logout?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowLogoutModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLogoutConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200 cursor-pointer"
            >
              Yes, Logout
            </button>
          </div>
        </div>
      </Modal>

      {/* Start Over Confirmation Modal */}
      <Modal
        isOpen={showStartOverModal}
        onClose={() => setShowStartOverModal(false)}
        title="Start Onboarding Over?"
      >
        <div className="space-y-4">
          <p className="text-gray-700 text-sm">
            This will clear all currently entered draft data and restart onboarding from Step 1. Are you sure you want to proceed?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowStartOverModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmStartOver}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 cursor-pointer"
            >
              Yes, Start Over
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OnboardingHeader;
